// Job storage utility for managing job applications in localStorage

const STORAGE_KEY = 'resume_architect_jobs';

export const saveJob = (job) => {
    const jobs = getAllJobs();
    const newJob = {
        id: Date.now().toString(),
        ...job,
        dateAdded: new Date().toISOString(),
        status: job.status || 'Interested'
    };
    jobs.push(newJob);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
    return newJob;
};

export const getAllJobs = () => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
};

export const updateJob = (id, updates) => {
    const jobs = getAllJobs();
    const index = jobs.findIndex(j => j.id === id);
    if (index !== -1) {
        jobs[index] = { ...jobs[index], ...updates };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
        return jobs[index];
    }
    return null;
};

export const deleteJob = (id) => {
    const jobs = getAllJobs().filter(j => j.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
};

export const getJobById = (id) => {
    return getAllJobs().find(j => j.id === id);
};

// Per-listing CV persistence — career-ops's pattern of generating a unique
// PDF per listing (`cv-candidate-{slug}-{date}.pdf`). Architect's tailored
// resume was previously ephemeral; this lets users freeze a snapshot of
// exactly what was sent for any application and re-download it later.
export const attachResumeSnapshot = (jobId, resumeData, template) => {
    return updateJob(jobId, {
        snapshot: {
            resumeData,
            template: template || 'modern',
            savedAt: new Date().toISOString()
        }
    });
};

export const clearResumeSnapshot = (jobId) => {
    return updateJob(jobId, { snapshot: null });
};

// ─── Pipeline integrity ─────────────────────────────────────────────────────
// Mirrors career-ops's dedup-tracker.mjs, normalize-statuses.mjs, and
// verify-pipeline.mjs — keeps the tracker clean as users accumulate
// hundreds of applications across months. Pure client-side, no API needed.

const CANONICAL_STATUSES = ['Interested', 'Applied', 'Interview', 'Offer', 'Rejected'];

// Common spellings/cases users (or older code) might have written.
const STATUS_ALIASES = {
    'interested': 'Interested', 'saved': 'Interested', 'todo': 'Interested', 'wishlist': 'Interested',
    'applied': 'Applied', 'submitted': 'Applied', 'application sent': 'Applied', 'in review': 'Applied',
    'interview': 'Interview', 'interviewing': 'Interview', 'phone screen': 'Interview', 'onsite': 'Interview', 'tech screen': 'Interview',
    'offer': 'Offer', 'offered': 'Offer', 'offer extended': 'Offer',
    'rejected': 'Rejected', 'declined': 'Rejected', 'not selected': 'Rejected', 'closed': 'Rejected', 'ghosted': 'Rejected'
};

const slug = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// Two jobs are considered duplicates when company + title both match
// (case- and punctuation-insensitive).
const fingerprint = (job) => `${slug(job.company)}::${slug(job.title)}`;

// Pick the "better" of two duplicate jobs to keep. Prefers: has analysis,
// has snapshot, more recent dateAdded, more advanced pipeline stage.
const STATUS_PRIORITY = { Offer: 5, Interview: 4, Applied: 3, Interested: 2, Rejected: 1 };
const score = (j) => (
    (j.analysis ? 10 : 0)
    + (j.snapshot ? 5 : 0)
    + (j.notes ? 2 : 0)
    + (j.matchScore ? Math.round(j.matchScore / 25) : 0)
    + (STATUS_PRIORITY[j.status] || 0)
    + (j.dateAdded ? (new Date(j.dateAdded).getTime() / 1e12) : 0)
);

export const dedupJobs = () => {
    const jobs = getAllJobs();
    const byFingerprint = new Map();

    for (const j of jobs) {
        const key = fingerprint(j);
        if (!key.includes('::') || key === '::') {
            // Can't fingerprint without company + title — leave it
            byFingerprint.set(`__keep-${j.id}`, j);
            continue;
        }
        const existing = byFingerprint.get(key);
        if (!existing) {
            byFingerprint.set(key, j);
        } else {
            // Merge: keep the higher-scored entry but graft missing fields from the other.
            const [winner, loser] = score(j) >= score(existing) ? [j, existing] : [existing, j];
            const merged = {
                ...loser,
                ...winner,
                // Field-level prefer-truthy merge
                link: winner.link || loser.link,
                notes: winner.notes || loser.notes,
                analysis: winner.analysis || loser.analysis,
                snapshot: winner.snapshot || loser.snapshot,
                matchScore: winner.matchScore || loser.matchScore,
                // Keep the EARLIER dateAdded so we don't lose application history
                dateAdded: loser.dateAdded && winner.dateAdded
                    ? (new Date(loser.dateAdded) < new Date(winner.dateAdded) ? loser.dateAdded : winner.dateAdded)
                    : (winner.dateAdded || loser.dateAdded)
            };
            byFingerprint.set(key, merged);
        }
    }

    const merged = Array.from(byFingerprint.values());
    const removed = jobs.length - merged.length;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    return { before: jobs.length, after: merged.length, removed };
};

export const normalizeStatuses = () => {
    const jobs = getAllJobs();
    let changed = 0;
    const updated = jobs.map(j => {
        const incoming = (j.status || '').toString().trim();
        if (CANONICAL_STATUSES.includes(incoming)) return j;
        const mapped = STATUS_ALIASES[incoming.toLowerCase()] || 'Interested';
        if (mapped !== incoming) changed++;
        return { ...j, status: mapped };
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return { total: jobs.length, changed };
};

// Find anomalies. Read-only — returns flags, does not mutate.
export const verifyTracker = () => {
    const jobs = getAllJobs();
    const issues = [];

    for (const j of jobs) {
        if (!j.title) issues.push({ jobId: j.id, severity: 'high', issue: 'Missing title' });
        if (!j.company) issues.push({ jobId: j.id, severity: 'high', issue: 'Missing company' });
        if (!j.dateAdded) issues.push({ jobId: j.id, severity: 'low', issue: 'Missing dateAdded' });
        if (j.status && !CANONICAL_STATUSES.includes(j.status)) {
            issues.push({ jobId: j.id, severity: 'medium', issue: `Non-canonical status: "${j.status}"` });
        }
        if (j.matchScore && (j.matchScore < 0 || j.matchScore > 100)) {
            issues.push({ jobId: j.id, severity: 'low', issue: `matchScore out of range: ${j.matchScore}` });
        }
        if (j.snapshot && !j.snapshot.resumeData) {
            issues.push({ jobId: j.id, severity: 'medium', issue: 'Empty snapshot payload' });
        }
    }

    // Cross-row: count duplicate fingerprints
    const counts = new Map();
    for (const j of jobs) {
        const key = fingerprint(j);
        if (!key.includes('::') || key === '::') continue;
        counts.set(key, (counts.get(key) || 0) + 1);
    }
    let duplicateGroups = 0;
    for (const [, n] of counts) if (n > 1) duplicateGroups++;

    return {
        total: jobs.length,
        issues,
        duplicateGroups,
        healthy: issues.length === 0 && duplicateGroups === 0
    };
};

// One-shot "clean everything" used by the Cleanup button.
export const runFullCleanup = () => {
    const verifyBefore = verifyTracker();
    const normalized = normalizeStatuses();
    const deduped = dedupJobs();
    const verifyAfter = verifyTracker();
    return { verifyBefore, normalized, deduped, verifyAfter };
};
